import { AutoPlanStatus, InvestorDetailsPacingTabCommitmentPlanQuery } from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import Button from '@/components/basicComponents/button';
import { Loader } from '@/components/loaders/loader';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { themeVar } from '@/styles/theme';
import { NetworkStatus, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { DELETE_COMMITMENT_PLAN_MUTATION, UPDATE_COMMITMENT_PLAN_MUTATION } from '../../queries';

const getCommitmentPlanStatus = (status: AutoPlanStatus) => {
  switch (status) {
    case AutoPlanStatus.InProgress:
      return {
        icon: <Loader size={60} />,
        title: 'Generating Commitment Plan',
        description:
          'This process may require a few minutes.  Feel free to leave this screen and return later.  Once your plan is complete, you have the option to manually adjust annual commitment amounts before saving your plan and viewing forecasted allocations and cashflows.'
      };
    case AutoPlanStatus.WaitingForReview:
      return {
        icon: <ApprovedIcon width={60} height={60} fill={themeVar('context.success')} />,
        title: 'Commitment Plan Ready for Review',
        description:
          'Your commitment plan is ready for your review.  Make changes to annual commitment amounts or, if everything looks good, save it as is to view forecasted allocations and cashflows.'
      };
    case AutoPlanStatus.Failed:
      return {
        icon: <InfoIcon width={60} height={60} style={{ transform: 'rotate(180deg)' }} fill={themeVar('context.error')} />,
        title: 'Auto Plan Error',
        description:
          'We were unable to construct a commitment plan that achieves your goals.  Try increasing the number of years allowed to achieve the allocation targets.  Or, if you prefer, you can manually enter annual commitment amounts.'
      };
    default:
      return {
        icon: <></>,
        title: '',
        description: ''
      };
  }
};

interface CommitmentPlanStatusesProps {
  status: AutoPlanStatus;
  currentCommitmentPlan: InvestorDetailsPacingTabCommitmentPlanQuery['commitmentPlan'] | null;
  commitmentPlanNetworkStatus: NetworkStatus;
  commitmentPlansRefetch: () => void;
  commitmentPlanRefetch: () => void;
}

const CommitmentPlanStatuses = ({
  status,
  currentCommitmentPlan,
  commitmentPlanNetworkStatus,
  commitmentPlansRefetch,
  commitmentPlanRefetch
}: CommitmentPlanStatusesProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const { searchParams } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';

  const isGroup = searchParams.get('isGroup') === 'true';

  const [updateCommitmentPlan, { loading: updateCommitmentPlanLoading }] = useMutation(UPDATE_COMMITMENT_PLAN_MUTATION, {
    onError: (error) => {
      console.error(error);
    }
  });

  const [deleteCommitmentPlan, { loading: deleteCommitmentPlanLoading }] = useMutation(DELETE_COMMITMENT_PLAN_MUTATION, {
    onError: (error) => {
      console.error(error);
    }
  });

  const startOver = () => {
    deleteCommitmentPlan({
      variables: {
        id: currentCommitmentPlan.id
      },
      onCompleted: () => {
        commitmentPlansRefetch();
      }
    });
  };

  const openEditCommitmentPlanPage = () => {
    if (!currentCommitmentPlan) return;

    navigate(
      `/investors/${investorId}/${entityId}/editCommitmentPlan?commitmentPlanId=${currentCommitmentPlan.id}&isGroup=${isGroup}&backToTitle=Investor Details`
    );
  };

  const changeAutoPlanStatus = (status: AutoPlanStatus) => {
    updateCommitmentPlan({
      variables: {
        input: {
          id: currentCommitmentPlan.id,
          autoPlanStatus: status
        }
      },
      onCompleted: () => {
        commitmentPlanRefetch();
      }
    });
  };

  return (
    <CommitmentPlanStatusWrapper>
      <CommitmentPlanStatus>
        {getCommitmentPlanStatus(status).icon}
        <div>
          <h2>{getCommitmentPlanStatus(status).title}</h2>
          <p>{getCommitmentPlanStatus(status).description}</p>
        </div>
      </CommitmentPlanStatus>
      {status === AutoPlanStatus.WaitingForReview && (
        <ButtonsWrapper>
          <Button
            onClick={() => changeAutoPlanStatus(AutoPlanStatus.Completed)}
            size="md"
            isLoading={updateCommitmentPlanLoading || commitmentPlanNetworkStatus === NetworkStatus.refetch}
            styleType={
              !updateCommitmentPlanLoading && commitmentPlanNetworkStatus !== NetworkStatus.refetch && currentCommitmentPlan ? 'default' : 'disabled'
            }
          >
            View Forecast
          </Button>
          <Button
            onClick={openEditCommitmentPlanPage}
            size="md"
            styleType={status === AutoPlanStatus.WaitingForReview && currentCommitmentPlan ? 'outline' : 'disabled'}
          >
            Edit Commitment Plan
          </Button>
        </ButtonsWrapper>
      )}
      {status === AutoPlanStatus.Failed && (
        <ButtonsWrapper>
          <Button
            onClick={startOver}
            size="md"
            isLoading={deleteCommitmentPlanLoading}
            styleType={deleteCommitmentPlanLoading ? 'disabled' : 'default'}
          >
            Start Over
          </Button>
          <Button onClick={openEditCommitmentPlanPage} size="md" styleType="outline">
            Create Plan Manually
          </Button>
        </ButtonsWrapper>
      )}
    </CommitmentPlanStatusWrapper>
  );
};

export default CommitmentPlanStatuses;

const CommitmentPlanStatusWrapper = styled.div`
  padding: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
`;

const CommitmentPlanStatus = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  color: ${themeVar('font.weak')};
  background-color: ${themeVar('layer.1')};
  border-radius: 20px;
  padding: 20px 80px;
  font-family: Lato;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
`;
